import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  viewChild,
  afterRenderEffect,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { DataService } from "../services/api/data/data.service";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { LoadingService } from "../services/loading/loading";
import {
  AdminData,
  Totals,
  CropAdmin,
  District,
  ActivityTrendClass,
  TimeseriesData,
} from "../models/admin.model";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DropdownComponent } from "../dashboard/utils/dropdown/dropdown.component";
import { ThemeService } from "../services/theme/theme.service";
import { BaseChartDirective } from "ng2-charts";
import {
  Chart,
  ChartData,
  ChartOptions,
  Plugin,
  ArcElement,
  DoughnutController,
  LineController,
  LineElement,
  BarController,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import "chartjs-adapter-date-fns";

Chart.register(
  ArcElement,
  DoughnutController,
  LineController,
  LineElement,
  BarController,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
);

// Color palette for doughnut chart slices
const DOUGHNUT_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
  "#84CC16",
  "#F97316",
  "#EC4899",
  "#6366F1",
  "#14B8A6",
  "#A855F7",
  "#E11D48",
  "#0EA5E9",
  "#D946EF",
  "#FBBF24",
];

@Component({
  selector: "app-admin",
  imports: [CommonModule, DropdownComponent, BaseChartDirective],
  templateUrl: "./admin.html",
  styleUrl: "./admin.scss",
  host: { class: "flex flex-col h-full" },
})
export class Admin {
  private dataService = inject(DataService);
  private loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private themeService = inject(ThemeService);

  private isDarkMode = toSignal(this.themeService.darkMode$);

  // Combo chart container ref for ResizeObserver
  comboChartContainer = viewChild<ElementRef<HTMLDivElement>>(
    "comboChartContainer",
  );
  isSmallComboContainer = signal(false);

  lineChartDropdownOptions: Array<{
    key: keyof ActivityTrendClass;
    value: string;
  }> = [
    { key: "combined", value: "All" },
    { key: "growers", value: "Growers" },
    { key: "managers", value: "Managers" },
    { key: "entities", value: "Entities" },
  ];
  lineChartSelectedOption = signal<keyof ActivityTrendClass>("combined");

  comboChartDropdownOptions: Array<{ key: string; value: string }> = [
    { key: "combo", value: "Chart View" },
    { key: "table", value: "Table View" },
  ];
  comboChartDropdownSelection = signal<string>("combo");
  districtTableData = computed<DistrictRow[]>(() => {
    if (!this.resources.hasValue()) return [];
    return this.resources.value().districts.map((d) => ({
      name: d.district_name,
      claimed: d.metrics.fields_claimed,
      total: d.metrics.fields_total,
      claimRate:
        Math.round((d.metrics.fields_claimed / d.metrics.fields_total) * 1000) /
        10,
    }));
  });
  districtTotals = computed(() => {
    const rows = this.districtTableData();
    const total = rows.reduce((sum, r) => sum + r.total, 0);
    const claimed = rows.reduce((sum, r) => sum + r.claimed, 0);
    const claimRate = total > 0 ? Math.round((claimed / total) * 1000) / 10 : 0;
    return { total, claimed, claimRate };
  });

  resources = rxResource({
    stream: () => this.dataService.getAdminStatisticsSummary(),
  });
  selectedDistrict = signal<string>("all districts");

  selectedDistrictData = computed<District | null>(() => {
    if (
      !this.resources.hasValue() ||
      this.selectedDistrict() === "all districts"
    ) {
      return null;
    }
    return (
      this.resources
        .value()
        .districts.find(
          (item) => item.district_name === this.selectedDistrict(),
        ) ?? null
    );
  });

  selectedDistrictMetrics = computed<Totals | null>(() => {
    if (!this.resources.hasValue()) {
      return null;
    }
    if (this.selectedDistrict() === "all districts") {
      return this.resources.value().totals;
    }
    return this.selectedDistrictData()?.metrics ?? null;
  });

  //! Dropdown data
  districtDropdownOptions = computed<Array<{ key: string; value: string }>>(
    () => {
      let districtDropdownOpts: Array<{ key: string; value: string }> = [
        {
          key: "all districts",
          value: "All Districts",
        },
      ];

      if (!this.resources.hasValue()) {
        return [];
      }
      const districtData = this.resources.value().districts;
      districtDropdownOpts.push(
        ...districtData.map((districtItem) => {
          return {
            key: districtItem.district_name,
            value: districtItem.district_name,
          };
        }),
      );
      return districtDropdownOpts;
    },
  );

  //! Card data
  adminCardData = computed<AdminCard[]>(() => {
    const districtMetricData = this.selectedDistrictMetrics();
    let adminCardMap: AdminCard[] = [];
    if (!districtMetricData) {
      return adminCardMap;
    }
    if (this.selectedDistrict() === "all districts") {
      adminCardMap = mapMetricToAdminCard(
        districtMetricData,
        "all districts",
        this.sanitizer,
      );
    } else {
      if (!districtMetricData) {
        console.error(
          `District data with name ${this.selectedDistrict()} is not found`,
        );
        return adminCardMap;
      }
      adminCardMap = mapMetricToAdminCard(
        districtMetricData,
        this.selectedDistrict(),
        this.sanitizer,
      );
    }

    return adminCardMap;
  });

  // ===================== DOUGHNUT CHART =====================

  // Custom legend items for the scrollable HTML legend
  pieLegendItems = computed<{ label: string; color: string; value: number }[]>(
    () => {
      const data = this.pieChartData();
      if (!data.labels?.length) return [];
      return (data.labels as string[]).map((label, i) => ({
        label,
        color: DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length],
        value: (data.datasets[0]?.data[i] as number) ?? 0,
      }));
    },
  );

  // Track which legend item is hovered so the template can highlight it
  hoveredPieLegendIndex = signal<number | null>(null);

  // ViewChild to access the doughnut chart directive for legend hover interaction
  pieChartDirective = viewChild("pieChartCanvas", { read: BaseChartDirective });

  onPieLegendHover(index: number) {
    this.hoveredPieLegendIndex.set(index);
    const directive = this.pieChartDirective();
    if (directive?.chart) {
      directive.chart.setActiveElements([{ datasetIndex: 0, index }]);
      directive.chart.update("none");
    }
  }

  onPieLegendLeave() {
    this.hoveredPieLegendIndex.set(null);
    const directive = this.pieChartDirective();
    if (directive?.chart) {
      directive.chart.setActiveElements([]);
      directive.chart.update("none");
    }
  }

  pieChartData = computed<ChartData<"doughnut">>(() => {
    if (!this.resources.hasValue())
      return { labels: [], datasets: [{ data: [] }] };

    let cropDistributionData: CropAdmin[] = [];
    if (this.selectedDistrict() === "all districts") {
      cropDistributionData =
        this.resources.value().crop_type_distribution.all_districts.crops;
    } else {
      cropDistributionData =
        this.selectedDistrictData()?.crop_type_distribution.crops ?? [];
    }

    const labels = cropDistributionData.map((c) =>
      abbreviateCropLabel(c.crop_type),
    );
    const series = cropDistributionData.map((c) => c.percentage);
    const dark = this.isDarkMode() ?? false;

    return {
      labels,
      datasets: [
        {
          data: series,
          backgroundColor: DOUGHNUT_COLORS.slice(0, series.length),
          borderColor: dark ? "#1f2937" : "#ffffff",
          borderWidth: 3,
          hoverOffset: 8,
          hoverBorderColor: dark ? "#ffffff" : "#000000",
          hoverBorderWidth: 3,
        },
      ],
    };
  });

  pieChartOptions = computed<ChartOptions<"doughnut">>(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      layout: {
        padding: { top: 4, bottom: 4 },
      },
      plugins: {
        legend: {
          display: false, // Using custom HTML legend on the right
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.label}: ${Number(ctx.parsed).toFixed(1)}%`,
          },
        },
      },
      onHover: (_event, elements, chart) => {
        if (elements.length > 0 || chart.getActiveElements().length > 0) {
          chart.draw();
        }
      },
    };
  });

  // Plugin to draw center label inside the doughnut
  pieChartPlugins: Plugin<"doughnut">[] = [
    {
      id: "doughnutCenterLabel",
      afterDraw: (chart) => {
        const { ctx } = chart;
        const activeElements = chart.getActiveElements();
        const dark = this.isDarkMode() ?? false;
        const textColor = dark ? "#f1f5f9" : "#1f2937";

        let label: string;
        let value: string;

        if (activeElements.length > 0) {
          const idx = activeElements[0].index;
          label = (chart.data.labels?.[idx] as string) ?? "";
          value = `${Number(chart.data.datasets[0].data[idx]).toFixed(1)}%`;
        } else {
          label = "Total Fields";
          const total = (chart.data.datasets[0].data as number[]).reduce(
            (a, b) => a + (b ?? 0),
            0,
          );
          value = `${total.toFixed(1)}%`;
        }

        const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Truncate long labels
        const maxLabelWidth =
          (chart.chartArea.right - chart.chartArea.left) * 0.5;
        ctx.font = "14px Inter, sans-serif";
        ctx.fillStyle = textColor;
        let displayLabel = label;
        while (
          ctx.measureText(displayLabel).width > maxLabelWidth &&
          displayLabel.length > 3
        ) {
          displayLabel = displayLabel.slice(0, -4) + "...";
        }
        ctx.fillText(displayLabel, centerX, centerY - 12);

        ctx.font = "bold 26px Inter, sans-serif";
        ctx.fillStyle = textColor;
        ctx.fillText(value, centerX, centerY + 14);
        ctx.restore();
      },
    },
  ];

  // ===================== LINE CHART =====================

  lineChartData = computed<ChartData<"line">>(() => {
    if (!this.resources.hasValue()) return { labels: [], datasets: [] };

    const loginSeries: number[] = [];
    const claimSeries: number[] = [];
    const updateSeries: number[] = [];
    const xSeries: string[] = [];

    const mapActivityToSeries = (activity: TimeseriesData[]) => {
      activity.forEach((item) => {
        loginSeries.push(item.logins);
        claimSeries.push(item.claims);
        updateSeries.push(item.updates);
        xSeries.push(item.date as unknown as string);
      });
    };

    if (this.selectedDistrict() === "all districts") {
      const activityTrendAll =
        this.resources.value().activity_trend.all_districts;
      mapActivityToSeries(activityTrendAll[this.lineChartSelectedOption()]);
    } else {
      const activityTrendDistrict =
        this.selectedDistrictData()?.activity_trend ?? null;
      if (!activityTrendDistrict) return { labels: [], datasets: [] };
      mapActivityToSeries(
        activityTrendDistrict[this.lineChartSelectedOption()],
      );
    }

    const selectedOption = this.lineChartSelectedOption();

    // Per-role visible metrics:
    // - entities: logins only
    // - managers: logins + claims
    // - growers & combined: logins + claims + updates
    const showClaims = selectedOption !== "entities";
    const showUpdates =
      selectedOption !== "entities" && selectedOption !== "managers";

    const datasets: ChartData<"line">["datasets"] = [
      {
        data: loginSeries,
        label: "Logins",
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ];

    if (showClaims) {
      datasets.push({
        data: claimSeries,
        label: "Claims",
        borderColor: "#10B981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
      });
    }

    if (showUpdates) {
      datasets.push({
        data: updateSeries,
        label: "Updates",
        borderColor: "#F59E0B",
        backgroundColor: "rgba(245, 158, 11, 0.1)",
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
      });
    }

    return { labels: xSeries, datasets };
  });

  lineChartOptions = computed<ChartOptions<"line">>(() => {
    const dark = this.isDarkMode() ?? false;
    const textColor = dark ? "#f1f5f9" : "#1f2937";
    const gridColor = dark ? "#334155" : "#e5e7eb";

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        x: {
          type: "time" as const,
          time: { unit: "day", tooltipFormat: "MMM d, yyyy" },
          ticks: { color: textColor, font: { family: "Inter, sans-serif" } },
          grid: { color: gridColor },
        },
        y: {
          ticks: {
            color: textColor,
            font: { family: "Inter, sans-serif" },
            stepSize: 1,
          },
          grid: { color: gridColor },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: { display: false },
      },
    };
  });

  // ===================== COMBO CHART =====================

  private comboChartFullNames: string[] = [];

  // Using 'any' for datasets because Chart.js mixed chart types (bar + line) don't align with strict ChartData<'bar'>
  comboChartData = computed(() => {
    if (!this.resources.hasValue()) return { labels: [], datasets: [] };

    const totalFields: number[] = [];
    const claimedFields: number[] = [];
    const claimedRates: number[] = [];
    const xSeries: string[] = [];

    this.resources.value().districts.forEach((district) => {
      const metric = district.metrics;
      totalFields.push(metric.fields_total);
      claimedFields.push(metric.fields_claimed);
      claimedRates.push((metric.fields_claimed / metric.fields_total) * 100);
      xSeries.push(district.district_name);
    });

    this.comboChartFullNames = xSeries;
    const abbreviatedLabels = xSeries.map((name) =>
      abbreviateDistrictName(name),
    );

    if (this.isSmallComboContainer()) {
      return {
        labels: abbreviatedLabels,
        datasets: [
          {
            data: totalFields,
            label: "Total Fields",
            backgroundColor: "#3B82F6",
            borderRadius: 3,
          },
          {
            data: claimedFields,
            label: "Claimed Fields",
            backgroundColor: "#93C5FD",
            borderRadius: 3,
          },
        ],
      };
    }

    return {
      labels: abbreviatedLabels,
      datasets: [
        {
          data: totalFields,
          label: "Total Fields",
          type: "bar" as const,
          backgroundColor: "#3B82F6",
          borderRadius: 3,
          yAxisID: "y",
          order: 2,
        },
        {
          data: claimedFields,
          label: "Claimed Fields",
          type: "bar" as const,
          backgroundColor: "#93C5FD",
          borderRadius: 3,
          yAxisID: "y",
          order: 3,
        },
        {
          data: claimedRates,
          label: "Claim Rate (%)",
          type: "line" as const,
          borderColor: "#F59E0B",
          backgroundColor: "rgba(245, 158, 11, 0.1)",
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: "y1",
          order: 1,
        },
      ],
    };
  });

  comboChartOptions = computed<ChartOptions<"bar">>(() => {
    const dark = this.isDarkMode() ?? false;
    const textColor = dark ? "#f1f5f9" : "#1f2937";
    const gridColor = dark ? "#334155" : "#e5e7eb";
    const fullNames = this.comboChartFullNames;

    if (this.isSmallComboContainer()) {
      return {
        indexAxis: "y" as const,
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { family: "Inter, sans-serif", size: 10 },
            },
            grid: { color: gridColor },
          },
          y: {
            ticks: {
              color: textColor,
              font: { family: "Inter, sans-serif", size: 10 },
            },
            grid: { color: gridColor },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: "index" as const,
            intersect: false,
            callbacks: {
              title: (items) =>
                fullNames[items[0]?.dataIndex] ?? items[0]?.label,
            },
          },
        },
      };
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          ticks: {
            color: textColor,
            font: { family: "Inter, sans-serif", size: 13 },
            maxRotation: 45,
          },
          grid: { color: gridColor },
        },
        y: {
          type: "linear" as const,
          position: "left" as const,
          title: {
            display: true,
            text: "Number of Fields",
            color: textColor,
            font: { family: "Inter, sans-serif", size: 13 },
          },
          ticks: {
            color: textColor,
            font: { family: "Inter, sans-serif", size: 13 },
            callback: (val) => Math.round(Number(val)).toLocaleString(),
          },
          grid: { color: gridColor },
        },
        y1: {
          type: "linear" as const,
          position: "right" as const,
          title: {
            display: true,
            text: "Claim Rate (%)",
            color: textColor,
            font: { family: "Inter, sans-serif", size: 13 },
          },
          ticks: {
            color: textColor,
            font: { family: "Inter, sans-serif", size: 13 },
            callback: (val) => `${Math.round(Number(val))}%`,
          },
          min: 0,
          max: 100,
          grid: { drawOnChartArea: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: "index" as const,
          intersect: false,
          callbacks: {
            title: (items) => fullNames[items[0]?.dataIndex] ?? items[0]?.label,
            label: (ctx) => {
              if (ctx.datasetIndex === 2)
                return ` ${ctx.dataset.label}: ${Math.round(Number(ctx.parsed.y))}%`;
              return ` ${ctx.dataset.label}: ${Math.round(Number(ctx.parsed.y)).toLocaleString()}`;
            },
          },
        },
      },
    };
  });

  // Plugin to draw data labels on the Claim Rate line (dataset index 2)
  comboChartPlugins: Plugin<"bar">[] = [
    {
      id: "claimRateLabels",
      afterDatasetsDraw: (chart) => {
        const meta = chart.getDatasetMeta(2);
        if (!meta || meta.hidden || meta.type !== "line") return;

        const { ctx } = chart;

        ctx.save();
        ctx.font = "bold 12px Inter, sans-serif";
        ctx.fillStyle = this.isDarkMode() ? "#f1f5f9" : "#1f2937";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.textBaseline = "bottom";

        meta.data.forEach((point, i) => {
          const value = chart.data.datasets[2]?.data[i];
          if (value == null) return;
          const label = `${Math.round(Number(value))}%`;
          ctx.fillText(label, point.x, point.y - 6);
        });
        ctx.restore();
      },
    },
  ];

  constructor() {
    this.loadingService.start({
      token: "admin-stats",
      message: "Loading admin statistics...",
    });

    // Update loading services
    effect(() => {
      if (this.resources.hasValue() || this.resources.error()) {
        this.loadingService.stop("admin-stats");
      }
    });

    // ResizeObserver for combo chart responsiveness
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      this.isSmallComboContainer.set(width < 1024);
    });

    afterRenderEffect({
      read: () => {
        const container = this.comboChartContainer();
        if (container) {
          resizeObserver.disconnect();
          resizeObserver.observe(container.nativeElement);
        }
      },
    });

    this.destroyRef.onDestroy(() => {
      this.loadingService.stop("admin-stats");
      resizeObserver.disconnect();
    });
  }
}

type AdminCardKey = Exclude<
  keyof AdminData["totals"],
  "fields_total" | "registered_growers"
>;
interface AdminCard {
  title: string;
  value: number;
  secondaryValue?: number;
  subtitle: string;
  icon: SafeHtml;
  iconBg: string;
  accentBorder: string;
}

type RawAdminCard = Omit<AdminCard, "value" | "icon" | "secondaryValue"> & {
  icon: string;
  iconBg: string;
  accentBorder: string;
};

const mapMetricToAdminCard = (
  totalData: Totals,
  districtName: string,
  sanitizer: DomSanitizer,
) => {
  const preprocessedData = getAdminSubIconMap(districtName);
  return Object.entries(totalData)
    .filter(([key]) => key in preprocessedData)
    .map(([key, value]) => {
      const { icon, ...rest } = preprocessedData[key as AdminCardKey];
      const card: AdminCard = {
        value,
        ...rest,
        icon: sanitizer.bypassSecurityTrustHtml(icon),
      };
      if (key === "total_growers") {
        card.secondaryValue = totalData.registered_growers;
      }
      return card;
    });
};

const getAdminSubIconMap = (
  districtName: string,
): Record<AdminCardKey, RawAdminCard> => ({
  total_growers: {
    title: "Active / Total Growers",
    subtitle: "Active registered users",
    iconBg: "bg-blue-100 dark:bg-blue-900/30",
    accentBorder: "border-l-blue-500",
    icon: '<svg class="w-5 h-5 text-blue-600 dark:text-blue-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M12 6a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm-1.5 8a4 4 0 0 0-4 4 2 2 0 0 0 2 2h7a2 2 0 0 0 2-2 4 4 0 0 0-4-4h-3Zm6.82-3.096a5.51 5.51 0 0 0-2.797-6.293 3.5 3.5 0 1 1 2.796 6.292ZM19.5 18h.5a2 2 0 0 0 2-2 4 4 0 0 0-4-4h-1.1a5.503 5.503 0 0 1-.471.762A5.998 5.998 0 0 1 19.5 18ZM4 7.5a3.5 3.5 0 0 1 5.477-2.889 5.5 5.5 0 0 0-2.796 6.293A3.501 3.501 0 0 1 4 7.5ZM7.1 12H6a4 4 0 0 0-4 4 2 2 0 0 0 2 2h.5a5.998 5.998 0 0 1 3.071-5.238A5.505 5.505 0 0 1 7.1 12Z" clip-rule="evenodd"/></svg>',
  },
  active_managers: {
    title: "Managers",
    subtitle: "Platform administrators",
    iconBg: "bg-purple-100 dark:bg-purple-900/30",
    accentBorder: "border-l-purple-500",
    icon: '<svg class="w-5 h-5 text-purple-600 dark:text-purple-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15v4m6-6v6m6-4v4m6-6v6M3 11l6-5 6 5 5.5-5.5"/></svg>',
  },
  fields_claimed: {
    title: "Fields Claimed",
    subtitle: getFieldsClaimedSubtitle(districtName),
    iconBg: "bg-green-100 dark:bg-green-900/30",
    accentBorder: "border-l-green-500",
    icon: '<svg class="w-5 h-5 text-green-600 dark:text-green-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M11.906 1.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L5.982 15.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM15 9.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z" clip-rule="evenodd"/></svg>',
  },
  unique_crop_types: {
    title: "Crop Types",
    subtitle: "Different crop types",
    iconBg: "bg-amber-100 dark:bg-amber-900/30",
    accentBorder: "border-l-amber-500",
    icon: '<svg class="w-5 h-5 text-amber-600 dark:text-amber-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.54025 15.4598c-.15524.0503-.82936-1.9171-.89837-2.3619-.08825-.5688.03111-1.7696.15081-1.7963.1197-.0267.68672 1.2412.75573 1.686.06901.4447.14706 2.4219-.00817 2.4722Zm0 0c-.00841-.163 2.06645-.3049 2.51395-.2565.5722.062 1.7012.4881 1.696.6106-.0052.1225-1.3766.3421-1.8241.2936-.4475-.0484-2.37745-.4848-2.38585-.6477Zm3.71475-3.7147L4 20m8.255-8.2549 6.6038-6.6039m-6.6038 6.6039c-.1553.0503-.8294-1.91714-.8984-2.36191-.0883-.56876.0311-1.76956.1508-1.79626.1197-.0267.6867 1.24116.7557 1.68593.069.44477.1471 2.42194-.0081 2.47224Zm0 0c-.0084-.163 2.0664-.3049 2.5139-.2565.5722.062 1.7012.4881 1.696.6106-.0052.1225-1.3766.3421-1.8241.2936-.4475-.0484-2.3774-.4848-2.3858-.6477ZM20 8.95298l-1.2713 1.27132m-3.6816-6.22427-1.2713 1.27129M5.26953 18.7305c-.15523.0503-.82935-1.9171-.89836-2.3619-.08825-.5687.0311-1.7696.1508-1.7963.1197-.0267.68673 1.2412.75574 1.686.06901.4447.14705 2.4219-.00818 2.4722Zm0 0c-.0084-.163 2.06645-.3049 2.51393-.2565.57221.062 1.70121.4881 1.69602.6106-.00519.1226-1.37661.3421-1.82408.2937-.44748-.0485-2.37746-.4848-2.38587-.6478Z"/></svg>',
  },
});

const getFieldsClaimedSubtitle = (districtName: string) => {
  if (districtName === "all districts") {
    return "Across all districts";
  }
  return `In ${districtName}`;
};

const abbreviateCropLabel = (label: string): string => {
  return label
    .replace(/\s*-\s*with cover crop$/i, " (CC)")
    .replace(/\s*-\s*without cover crop$/i, " (NCC)");
};

const abbreviateDistrictName = (name: unknown): string => {
  const str = String(name ?? "");
  const stopWords = new Set(["of", "the", "and", "&", "a"]);
  const words = str.split(/[\s-]+/);
  if (words.length <= 2) return str;
  return words
    .filter((w) => !stopWords.has(w.toLowerCase()))
    .map((w) => w[0].toUpperCase())
    .join("");
};

interface DistrictRow {
  name: string;
  claimed: number;
  total: number;
  claimRate: number;
}
